import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  registerUser,
  loginUser,
  getCurrentUser,
  validateAccessKey,
  updateUserPreferences,
} from '../api/rlApi';
import { useLocation } from 'react-router-dom';

function AuthPage() {
  const location = useLocation();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const [accessKey, setAccessKey] = useState('');
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [preferredChannel, setPreferredChannel] = useState('email');
  const [notifEmail, setNotifEmail] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefError, setPrefError] = useState('');
  const [prefMessage, setPrefMessage] = useState('');

  const loadCurrentUser = async () => {
    try {
      const res = await getCurrentUser();
      setUser(res.user);
      const n = res.user?.notifications || {};
      setPreferredChannel(n.preferred_channel || 'email');
      setNotifEmail(n.email || res.user?.email || '');
      setTelegramChatId(n.telegram_chat_id || '');
      setDiscordWebhookUrl(n.discord_webhook_url || '');
      setWhatsappNumber(n.whatsapp_number || '');
    } catch (e) {
      setUser(null);
    }
  };

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    const hash = (location.hash || '').toLowerCase();
    if (hash.includes('register')) {
      setMode('register');
    } else if (hash.includes('login')) {
      setMode('login');
    }
  }, [location.hash]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('access_key');
    if (stored) {
      setAccessKey(stored);
      handleValidateKey(stored);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await registerUser({ email, password, username });
        window.localStorage.setItem('auth_token', res.token);
        setUser(res.user);
        setMessage('Registered and logged in');
        window.dispatchEvent(new Event('auth-updated'));
      } else {
        const res = await loginUser({ email, password });
        window.localStorage.setItem('auth_token', res.token);
        setUser(res.user);
        setMessage('Logged in');
        window.dispatchEvent(new Event('auth-updated'));
      }
    } catch (err) {
      setError(err?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateKey = async (value) => {
    const keyToUse = (value ?? accessKey).trim();
    if (!keyToUse) {
      setAccessStatus(null);
      setAccessError('');
      return;
    }
    setAccessLoading(true);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('access_key', keyToUse);
      }
      const res = await validateAccessKey();
      setAccessStatus(res);
      setAccessError('');
    } catch (err) {
      setAccessStatus(null);
      setAccessError(err?.message || 'Invalid access key');
    } finally {
      setAccessLoading(false);
    }
  };

  const handleClearKey = () => {
    setAccessKey('');
    setAccessStatus(null);
    setAccessError('');
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('access_key');
    }
  };

  const handleLogout = () => {
    window.localStorage.removeItem('auth_token');
    setUser(null);
    setMessage('Logged out');
    window.dispatchEvent(new Event('auth-updated'));
  };

  const handleSavePreferences = async () => {
    setPrefError('');
    setPrefMessage('');
    setPrefSaving(true);
    try {
      const payload = {
        preferred_channel: preferredChannel,
        email: notifEmail || undefined,
        telegram_chat_id: telegramChatId || undefined,
        discord_webhook_url: discordWebhookUrl || undefined,
        whatsapp_number: whatsappNumber || undefined,
      };
      const res = await updateUserPreferences(payload);
      setUser(res.user);
      const n = res.user?.notifications || {};
      setPreferredChannel(n.preferred_channel || 'email');
      setPrefMessage('Notification preferences saved');
    } catch (err) {
      setPrefError(err?.message || 'Failed to save preferences');
    } finally {
      setPrefSaving(false);
    }
  };

  const loggedInView = user ? (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        background: 'var(--card)',
        borderColor: 'var(--border)',
        boxShadow: 'var(--glow)',
        color: 'var(--text)',
        width: '100%',
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle1">Current user</Typography>
        <Typography variant="body2">Email: {user.email}</Typography>
        <Typography variant="body2">Username: {user.username}</Typography>
        <Typography variant="body2">Account type: {user.account_type || 'basic'}</Typography>
        {user.created_at && (
          <Typography variant="body2" color="text.secondary">
            Member since: {new Date(user.created_at).toLocaleString()}
          </Typography>
        )}

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: '1px solid var(--border)',
            background: 'rgba(255,255,255,0.03)',
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Notification preferences
          </Typography>
          {prefError && <Alert severity="error">{prefError}</Alert>}
          {prefMessage && <Alert severity="success">{prefMessage}</Alert>}
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="preferred-channel-label">Preferred channel</InputLabel>
              <Select
                labelId="preferred-channel-label"
                label="Preferred channel"
                value={preferredChannel}
                onChange={(e) => setPreferredChannel(e.target.value)}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="telegram">Telegram</MenuItem>
                <MenuItem value="discord">Discord</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
              <FormHelperText>Select where to receive exports/alerts</FormHelperText>
            </FormControl>

            <TextField
              label="Email (fallback)"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
              fullWidth
              placeholder="you@example.com"
            />
            <TextField
              label="Telegram chat ID"
              value={telegramChatId}
              onChange={(e) => setTelegramChatId(e.target.value)}
              fullWidth
              placeholder="e.g., 123456789"
            />
            <TextField
              label="Discord webhook URL"
              value={discordWebhookUrl}
              onChange={(e) => setDiscordWebhookUrl(e.target.value)}
              fullWidth
              placeholder="https://discord.com/api/webhooks/..."
            />
            <TextField
              label="WhatsApp number"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              fullWidth
              placeholder="+15551234567"
            />
            <Button
              variant="contained"
              onClick={handleSavePreferences}
              disabled={prefSaving}
              fullWidth
            >
              {prefSaving ? 'Saving...' : 'Save preferences'}
            </Button>
          </Stack>
        </Box>

        {accessStatus?.entitlements && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid var(--border)',
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Production Access Key
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                Owner: {accessStatus.entitlements.owner_email || '—'}
              </Typography>
              <Typography variant="body2">
                Symbols:{' '}
                {accessStatus.entitlements.allowed_symbols?.length
                  ? accessStatus.entitlements.allowed_symbols.join(', ')
                  : 'Any'}
              </Typography>
              <Typography variant="body2">
                Timeframes:{' '}
                {accessStatus.entitlements.allowed_timeframes?.length
                  ? accessStatus.entitlements.allowed_timeframes.join(', ')
                  : 'Any'}
              </Typography>
              <Typography variant="body2">
                Timesteps: {accessStatus.entitlements.min_timesteps} - {accessStatus.entitlements.max_timesteps}
              </Typography>
              <Typography variant="body2">
                Generations: {accessStatus.entitlements.min_generations} - {accessStatus.entitlements.max_generations}
              </Typography>
              {typeof accessStatus.entitlements.max_training_runs === 'number' && (
                <Typography variant="body2">
                  Training runs remaining:{' '}
                  {Math.max(
                    0,
                    accessStatus.entitlements.max_training_runs - (accessStatus.usage?.training_runs ?? 0)
                  )}
                </Typography>
              )}
              {typeof accessStatus.entitlements.max_competitions === 'number' && (
                <Typography variant="body2">
                  Competitions remaining:{' '}
                  {Math.max(
                    0,
                    accessStatus.entitlements.max_competitions - (accessStatus.usage?.competitions_started ?? 0)
                  )}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        <Button onClick={handleLogout} variant="outlined" size="small">
          Logout
        </Button>
      </Stack>
    </Paper>
  ) : null;

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          textAlign: 'center',
          background: 'var(--card)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--glow)',
          color: 'var(--text)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="h4" component="h1">
            🔐 Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Register or log in to access protected features.
          </Typography>

          {!user && (
            <Tabs
              value={mode}
              onChange={(_, v) => setMode(v)}
              centered
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab value="login" label="Login" />
              <Tab value="register" label="Register" />
            </Tabs>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {message && <Alert severity="success">{message}</Alert>}

          {!user && (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Stack spacing={2} alignItems="center">
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                />
                {mode === 'register' && (
                  <TextField
                    label="Username (optional)"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                  />
                )}
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  helperText={mode === 'register' ? 'Minimum 8 characters' : ''}
                />
                <Button variant="contained" type="submit" disabled={loading} fullWidth>
                  {loading ? 'Working...' : mode === 'register' ? 'Register' : 'Login'}
                </Button>
              </Stack>
            </Box>
          )}

          {loggedInView}
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mt: 3,
          borderRadius: 3,
          background: 'var(--card)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--glow)',
          color: 'var(--text)',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h5" component="h2" textAlign="center">
            🔑 Production Access Key
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Enter the access key provided to you to unlock training, models, and competitions.
          </Typography>

          {accessError && <Alert severity="error">{accessError}</Alert>}
          {!accessError && !accessStatus && !accessKey && (
            <Alert
              severity="info"
              variant="outlined"
              sx={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'inherit' }}
            >
              No access key saved yet. Enter and save a key to unlock training, models, and competitions.
            </Alert>
          )}
          {accessStatus && (
            <Alert severity="success">
              Access key valid. Allowed timeframes:{' '}
              {accessStatus.entitlements?.allowed_timeframes?.length
                ? accessStatus.entitlements.allowed_timeframes.join(', ')
                : 'Any'}; Symbols:{' '}
              {accessStatus.entitlements?.allowed_symbols?.length
                ? accessStatus.entitlements.allowed_symbols.join(', ')
                : 'Any'}; Timesteps {accessStatus.entitlements?.min_timesteps} -{' '}
              {accessStatus.entitlements?.max_timesteps}; Generations{' '}
              {accessStatus.entitlements?.min_generations} -{' '}
              {accessStatus.entitlements?.max_generations}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
            <TextField
              label="Access Key"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              fullWidth
              placeholder="Enter your production access key"
            />
            <Button
              variant="contained"
              onClick={() => handleValidateKey()}
              disabled={accessLoading}
              sx={{ minWidth: 120 }}
            >
              {accessLoading ? 'Validating...' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={handleClearKey} disabled={accessLoading} sx={{ minWidth: 100 }}>
              Clear
            </Button>
          </Stack>

          {accessStatus?.entitlements && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid var(--border)',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Key Entitlements
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  Owner: {accessStatus.entitlements.owner_email || '—'}
                </Typography>
                <Typography variant="body2">
                  Symbols:{' '}
                  {accessStatus.entitlements.allowed_symbols?.length
                    ? accessStatus.entitlements.allowed_symbols.join(', ')
                    : 'Any'}
                </Typography>
                <Typography variant="body2">
                  Timeframes:{' '}
                  {accessStatus.entitlements.allowed_timeframes?.length
                    ? accessStatus.entitlements.allowed_timeframes.join(', ')
                    : 'Any'}
                </Typography>
                <Typography variant="body2">
                  Timesteps: {accessStatus.entitlements.min_timesteps} -{' '}
                  {accessStatus.entitlements.max_timesteps}
                </Typography>
                <Typography variant="body2">
                  Generations: {accessStatus.entitlements.min_generations} -{' '}
                  {accessStatus.entitlements.max_generations}
                </Typography>
                {typeof accessStatus.entitlements.max_training_runs === 'number' && (
                  <Typography variant="body2">
                    Training runs remaining:{' '}
                    {Math.max(
                      0,
                      accessStatus.entitlements.max_training_runs -
                        (accessStatus.usage?.training_runs ?? 0)
                    )}
                  </Typography>
                )}
                {typeof accessStatus.entitlements.max_competitions === 'number' && (
                  <Typography variant="body2">
                    Competitions remaining:{' '}
                    {Math.max(
                      0,
                      accessStatus.entitlements.max_competitions -
                        (accessStatus.usage?.competitions_started ?? 0)
                    )}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}

export default AuthPage;

